import {
  faBars,
  faChevronDown,
  faChevronUp,
  faEye,
  faEyeLowVision,
  faPencil,
  faRotateLeft,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, IconButton, useTheme } from "@mui/material";
import { Children, type ReactNode, memo, useState } from "react";
import {
  DragDropContext,
  type DraggableProvidedDragHandleProps,
  type DropResult,
  Droppable,
} from "react-beautiful-dnd";

interface MenuAccordionProps {
  readonly children: ReactNode;
  readonly dragHandleProps?: DraggableProvidedDragHandleProps | null;
  readonly hidden?: boolean;
  readonly icon?: ReactNode;
  readonly name: string;
  readonly onDragItems: (result: DropResult) => void;
  readonly onRename?: (name: string) => void;
  readonly onToggleOpen: (open: boolean) => void;
  readonly onToggleSelection: () => void;
  readonly onToggleVisibility?: () => void;
  readonly open: boolean;
  readonly selected: boolean;
  readonly showDragHandle?: boolean;
  readonly showRenameButton?: boolean;
  readonly showVisibilityButton?: boolean;
  readonly locked?: boolean;
  readonly menuRefs: {
    setRef: (key: string) => (element: HTMLElement | null) => void;
  };
  readonly primaryFilter: string;
  readonly assetCategoryFilterId: number | null;
}

const styles = {
  container: {
    borderRadius: "8px",
    overflow: "hidden",
    color: "white",
    marginBottom: "8px",
  },
  containerOpen: {
    background: "rgb(46, 46, 46)",
  },
  childrenContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "0px 16px",
  },
  title: {
    alignItems: "center",
    display: "flex",
    height: "40px",
    boxSizing: "content-box",
    padding: "0px 8px",
    "& .showOnHover": {
      opacity: 0,
      position: "absolute",
      width: "0",
      height: "0",
      "&:focus": {
        position: "relative",
        opacity: "1",
        width: "auto",
        height: "auto",
      },
    },
    "& .showOnHoverOpacity": {
      opacity: 0,
      "&:focus": {
        opacity: 1,
      },
    },
    "&:hover": {
      "& .showOnHover": {
        position: "relative",
        opacity: "1",
        width: "auto",
        height: "auto",
      },
      "& .showOnHoverOpacity": {
        opacity: 1,
      },
    },
  },
  buttonName: {
    flex: 1,
    fontSize: 16,
    justifyContent: "flex-start",
    textAlign: "left",
    textWrap: "nowrap",
    cursor: "pointer",
    overflow: "hidden",
    display: "flex",
    color: "#fff",
    padding: "0px",
  },
  buttonIcon: {
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
    fontSize: "12px",
    height: "100%",
    minWidth: "30px",
    width: "30px",
    cursor: "pointer",
    color: "#fff",
    padding: "0px",
  },
  dragHandle: {
    marginRight: "8px",
    fontSize: "12px",
    width: "24px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: "none",
  },
  input: {
    background: "transparent",
    outline: 0,
    border: 0,
    borderBottom: "1px solid white",
    flex: 1,
    width: "100%",
    color: "white",
    fontSize: 16,
  },
  selected: (color: string) => ({
    color,
    "& *": {
      color: "inherit !important",
    },
  }),
};

function MenuAccordion(props: MenuAccordionProps) {
  const {
    children,
    dragHandleProps,
    hidden,
    icon,
    name,
    onRename,
    onToggleOpen,
    onToggleSelection,
    onToggleVisibility,
    onDragItems,
    open,
    selected,
    showDragHandle,
    showRenameButton,
    showVisibilityButton,
    locked = false,
    menuRefs,
    primaryFilter,
    assetCategoryFilterId,
  } = props;

  const [renaming, setRenaming] = useState(false);
  const [inputName, setInputName] = useState(name || "");
  const [error, setError] = useState("");
  const theme = useTheme();
  const color = (theme as unknown as { primaryColor: string }).primaryColor;

  const restore = () => {
    setRenaming(false);
  };

  return (
    <Box sx={[styles.container, open && styles.containerOpen]}>
      <Box sx={[styles.title, open && { borderBottom: "2px solid black" }]}>
        <Box
          data-testid="drag-handle"
          className="showOnHoverOpacity"
          sx={[styles.dragHandle, (renaming || !showDragHandle) && { display: "none" }]}
          {...dragHandleProps}
        >
          <FontAwesomeIcon icon={faBars} />
        </Box>

        {renaming ? (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ flex: 1, marginRight: "8px" }}>
                <input
                  style={styles.input}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !error) {
                      setRenaming(false);
                      onRename?.(inputName);
                    }
                  }}
                  value={inputName}
                  onChange={(e) => {
                    setInputName(e.target.value);
                    !e.target.value && setError("Too short");
                  }}
                />
              </Box>

              <IconButton
                data-testid="rename-confirm-button"
                sx={[styles.buttonIcon, { fontSize: 16 }]}
                onClick={restore}
              >
                <FontAwesomeIcon icon={faRotateLeft} />
              </IconButton>
            </Box>
            <Box sx={{ fontSize: 12, color: "#ff0000" }}>{error}</Box>
          </Box>
        ) : (
          <>
            <Button
              ref={menuRefs.setRef(`filter-menu-${primaryFilter}-${assetCategoryFilterId}`)}
              data-testid="name-button"
              sx={[styles.buttonName, hidden ? { opacity: 0.5 } : {}, selected && styles.selected(color)]}
              onClick={onToggleSelection}
            >
              {icon && <Box sx={[styles.dragHandle, { fontSize: "16px" }]}>{icon}</Box>}
              <Box sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{name}</Box>
            </Button>

            {showRenameButton && (
              <IconButton
                data-testid="rename-button"
                className="showOnHover"
                sx={styles.buttonIcon}
                onClick={() => {
                  if (locked) return;

                  setRenaming(true);
                }}
              >
                <FontAwesomeIcon icon={faPencil} />
              </IconButton>
            )}

            {showVisibilityButton && (
              <IconButton
                data-testid="visibility-button"
                className="showOnHover"
                sx={styles.buttonIcon}
                onClick={onToggleVisibility}
              >
                <FontAwesomeIcon icon={hidden ? faEyeLowVision : faEye} />
              </IconButton>
            )}

            <IconButton
              data-testid="expand-button"
              sx={[styles.buttonIcon, { fontSize: 16 }]}
              onClick={() => onToggleOpen(!open)}
            >
              <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
            </IconButton>
          </>
        )}
      </Box>

      <Accordion expanded={open || false} sx={{ margin: "0px !important", background: "transparent" }}>
        <AccordionSummary sx={{ display: "none" }} />
        <AccordionDetails sx={styles.childrenContainer}>
          <Box>
            <DragDropContext onDragEnd={onDragItems}>
              <Droppable droppableId="left-hand-menu">
                {(provided) => (
                  <Box
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    sx={{ height: `${Children.count(children) * 40}px` }}
                  >
                    {children}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default memo(MenuAccordion);
